import { jwtEmitidoEnMs, jwtEstaVigente } from './jwt-session.util';

/** Claves de sesión persistidas junto al JWT. */
export const AUTH_STORAGE_KEYS = {
  token: 'sc_jwt',
  permisos: 'sc_permisos_json',
  nombre: 'sc_nombre_display',
  rolId: 'sc_rol_id',
  rolNombre: 'sc_rol_nombre',
  userId: 'sc_user_id',
  organizacion: 'sc_organizacion_json',
} as const;

export type AuthSessionBucket = 'localStorage' | 'sessionStorage';

const ALL_KEYS = Object.values(AUTH_STORAGE_KEYS);

function store(bucket: AuthSessionBucket): Storage {
  return bucket === 'localStorage' ? localStorage : sessionStorage;
}

export function limpiarSesionEnBucket(bucket: AuthSessionBucket): void {
  const s = store(bucket);
  for (const key of ALL_KEYS) {
    s.removeItem(key);
  }
}

export function limpiarSesionEnAmbosBuckets(): void {
  limpiarSesionEnBucket('localStorage');
  limpiarSesionEnBucket('sessionStorage');
}

function tokenVigenteEnBucket(bucket: AuthSessionBucket): string | null {
  const token = store(bucket).getItem(AUTH_STORAGE_KEYS.token);
  if (!token?.trim()) return null;
  return jwtEstaVigente(token) ? token.trim() : null;
}

function limpiarTokenCaducadoEnBucket(bucket: AuthSessionBucket): void {
  const token = store(bucket).getItem(AUTH_STORAGE_KEYS.token);
  if (token?.trim() && !jwtEstaVigente(token)) {
    limpiarSesionEnBucket(bucket);
  }
}

/**
 * Resuelve el bucket activo de la sesión.
 * Ignora JWT caducados y, si hay tokens distintos en ambos buckets,
 * prioriza el vigente (evita que un resto en sessionStorage invalide «Recordarme»).
 */
export function resolverBucketSesionActiva(): AuthSessionBucket | null {
  if (typeof localStorage === 'undefined' || typeof sessionStorage === 'undefined') {
    return null;
  }

  limpiarTokenCaducadoEnBucket('localStorage');
  limpiarTokenCaducadoEnBucket('sessionStorage');

  const localToken = tokenVigenteEnBucket('localStorage');
  const sessionToken = tokenVigenteEnBucket('sessionStorage');

  if (sessionToken && localToken && sessionToken !== localToken) {
    const bucket =
      jwtEmitidoEnMs(localToken) > jwtEmitidoEnMs(sessionToken) ? 'localStorage' : 'sessionStorage';
    limpiarSesionEnBucket(bucket === 'localStorage' ? 'sessionStorage' : 'localStorage');
    return bucket;
  }
  if (sessionToken) return 'sessionStorage';
  if (localToken) return 'localStorage';
  return null;
}

/** Limpia ambos buckets y devuelve dónde persistir la nueva sesión. */
export function iniciarBucketSesion(recordarme: boolean): AuthSessionBucket {
  limpiarSesionEnAmbosBuckets();
  return recordarme ? 'localStorage' : 'sessionStorage';
}

export function escribirItemEnBucket(
  bucket: AuthSessionBucket,
  key: string,
  value: string,
): void {
  store(bucket).setItem(key, value);
}

/** Lee del mismo bucket que el JWT activo (evita mezclar admin en local + vendedor en session). */
export function leerItemSesionActiva(key: string): string | null {
  const bucket = resolverBucketSesionActiva();
  if (bucket) return store(bucket).getItem(key);
  return localStorage.getItem(key) ?? sessionStorage.getItem(key);
}
